import { client } from 'config/environment'
import { PoweredOptions } from 'feature/footer/PoweredOptions'
import { session } from 'feature/widget/SessionStore'
import { try_on } from 'feature/widget/TryOnStore'
import { ym } from 'feature/yama'
import { action, makeAutoObservable, runInAction } from 'mobx'
import { boutique } from 'page/catalog/employee/store/BoutiqueController'
import { buyModal, BuyModalStore } from 'page/catalog/store/BuyModalStore'
import { CatalogFormStore } from 'page/catalog/store/CatalogFormStore'
import { itemPreview, ItemPreviewStore } from 'page/catalog/store/ItemPreviewStore'
import { OptionsStore } from 'page/catalog/store/OptionsStore'
import { OutfitsPreviewStore } from 'page/catalog/store/OutfitsPreviewStore'
import { UpdateStore } from 'page/catalog/store/UpdateStore'
import { WelcomeStore } from 'page/catalog/store/WelcomeStore'
import { WidgetStore } from 'page/catalog/store/WidgetStore'
import { api } from 'store/api'
import { mx } from 'store/base/mx'
import { opt } from 'store/opt'
import { DemoCatalogData, DemoCatalogParams } from 'type/demo'
import { Filters } from 'type/Filters'
import { Item } from 'type/Item'
import { Product } from 'type/Product'
import { fetchJson } from 'util/fetch'

export class CatalogStore {
  readonly data: DemoCatalogData
  readonly params: DemoCatalogParams
  readonly footer: PoweredOptions
  ready = false
  error = false
  private _welcome: WelcomeStore | null = null
  animated = false
  private _drawer = false
  private _welcome_sochi = true
  options = new OptionsStore(false)
  readonly form: CatalogFormStore
  readonly widget: WidgetStore
  readonly outfitsPreview: OutfitsPreviewStore
  private _search_open = false
  private update: UpdateStore

  constructor(data: DemoCatalogData, params: DemoCatalogParams) {
    this.data = data
    this.params = params
    this.footer = PoweredOptions.fromFooter(data.widget.footer)
    this.form = new CatalogFormStore(data.product, this.stand)
    this.widget = new WidgetStore()
    this.outfitsPreview = new OutfitsPreviewStore(this.widget)
    this.update = new UpdateStore(data.product, params.stand)
    makeAutoObservable(this)
    if (params.basic) {
      if (params.start) this.startBasic()
      else this.welcome = true
      if (this.form.categories.value.length) this._welcome_sochi = false
    }
  }

  get welcome(): WelcomeStore | null {
    return this._welcome
  }

  set welcome(welcome: boolean) {
    // на стартовом экране sochi welcome-стор нужен ради документов и таймаута
    const enabled = this.product.welcome?.enabled || this.welcome_sochi
    if (welcome && enabled) this._welcome = new WelcomeStore(this.product)
    else this._welcome = null
  }

  startBasic(product_id?: string) {
    const here = !product_id || this.product.product_id === product_id
    if (here) {
      if (client) session.start('')
      this.welcome = false
    } else {
      if (client) location.assign(`/${product_id}?basic&start`)
    }
  }

  get show_name(): boolean {
    // const { lang, widget } = this.data
    // const hide = lang === 'en' && widget.hide_name_for_en
    return true
  }

  get by_email(): boolean {
    return opt.by_email
  }

  get by_card(): boolean {
    return opt.by_card
  }

  get tsum(): boolean {
    return !!this.data.product.tsumDesign
  }

  get slug(): string {
    return this.data.slug
  }

  get stand(): boolean {
    return this.params.stand
  }

  get basic(): boolean {
    return this.params.basic
  }

  get dark(): boolean {
    return this.basic && opt.sochi
  }

  /** Для sochi стартовый экран с выбором категории заменяет welcome */
  get welcome_sochi(): boolean {
    return this.basic && opt.sochi && this._welcome_sochi
  }

  pickCategory(category: string | null) {
    this.form.categories.value = category ? [category] : []
    this._welcome_sochi = false
    if (this._welcome) this.startBasic()
    this.form.trigger()
  }

  get boutique(): boolean {
    return this.params.boutique
  }

  get turn(): number {
    return this.params.turn
  }

  get product(): Product {
    return this.data.product
  }

  get drawer(): boolean {
    return this._drawer
  }

  set drawer(value: boolean) {
    this._drawer = value
  }

  get search_open(): boolean {
    return this._search_open
  }

  set search_open(value: boolean) {
    this._search_open = value
  }

  async startup() {
    try {
      this.update.startup()
      this.initTryon()
      void this.initFilters()
      void this.initItems()

      if (this.boutique) {
        void boutique.startup(this.product.company_id, this.data)
      }

      runInAction(() => {
        this.animated = true
        this.ready = true
      })
      setTimeout(action(() => {
        this.animated = false
      }))
    } catch (e) {
      console.error('startup failed', e)
      runInAction(() => {
        this.error = true
      })
    }
  }

  destroy() {
    this.update.destroy()
    this.outfitsPreview.destroy()
  }

  private initTryon() {
    const widget = this.data.widget
    const { params, product } = this

    try_on.init({ params, widget, window: product })
    try_on.onUpdate(update => this.widget.update(update))

    if (this.stand && !this.basic) {
      try_on.onUpdate(({ picture }) => {
        if (picture) session.start(picture)
      })
    }
  }

  get items() {
    return this.form.items
  }

  get shopUrl() {
    const { shopUrl } = this.product
    return shopUrl || this.shopUrlDefault
  }

  search() {
    const searchQuery = this.form.search.trim()
    if (searchQuery) {
      ym('search_items', { query: searchQuery })
    }
    this.form.refresh()
  }

  openBuyModal(item: Item) {
    buyModal.open(new BuyModalStore(item, this.show_name))
  }

  sendBuyEvent(object_id: string) {
    const name = 'click_buy_window'
    const origin = location.origin
    const { company_id, product_id } = this.product
    const access_key = this.data.widget.access
    const data = { company_id, access_key, origin, product_id }
    const event = { name, object_id, data }
    void api.sendEvent(event)
  }

  private async initItems() {
    await this.form.loadItems()
    this.selectItem()
  }

  private selectItem() {
    const params = new URLSearchParams(location.search)
    const item_id = params.get('item_id')
    if (item_id) {
      const item = this.form.items.find(it => it.item_id === item_id)
      if (item) itemPreview.open(new ItemPreviewStore(item, true))
    }
  }

  private async initFilters() {
    const { company_id } = this.product
    const group_id = this.product.group
    const request = JSON.stringify({ group_id, company_id })
    try {
      const filters = await fetchJson<Filters>('/api/stand/filters', { method: 'POST', body: request })
      runInAction(() => this.options = new OptionsStore(true, filters, this.product))
      await this.form.applyFilters(this.options)
      this.selectItem()
    } catch (e) {
      console.error('load filters failed', e)
      // без фильтров стартовый экран sochi остался бы на спиннере
      runInAction(() => this.options = new OptionsStore(true))
    }
  }

  private get shopUrlDefault() {
    const item = this.items[0]
    const link = item?.links || ''
    if (!link) return ''
    try {
      return new URL(link).origin
    } catch {
      return ''
    }
  }

  hasCombo(item: Item): boolean {
    const { combinations, dress_shape_type } = item
    const shape = ['top', 'bottom'].includes(dress_shape_type || '')
    const widgetCanCombine = this.data.widget.combine
    return !!combinations && shape && !!widgetCanCombine
  }

  canCombine(item: Item): boolean {
    const { combination } = this.product
    const { combinations, dress_shape_type } = item
    const shape = ['top', 'bottom'].includes(dress_shape_type || '')
    return !!combinations && shape && !this.basic && !!combination
  }

  canMix(item: Item): boolean {
    return this.hasCombo(item) && !this.boutique
  }

  canTry(item: Item): boolean {
    const { try_on_combo_only } = this.product
    const { dress_shape_type } = item
    return !try_on_combo_only || dress_shape_type === 'full'
  }

  isBuyButtonVisible(item: Item, details: boolean) {
    const buy = details ? this.product.details?.buy : this.product.items?.buy
    return buy && item.links && !this.boutique
  }

  async restart() {
    const restart = this.product.widget_auto_restart !== false
    itemPreview.close()
    if (this.basic) {
      this.welcome = true
      this._welcome_sochi = true
    }
    this.widget.reset()
    this.form.clear()
    if (restart) {
      await try_on.destroy()
      this.initTryon()
    } else {
      try_on.exit()
    }
  }
}

export const demo = mx.ref<CatalogStore>()

export function destroyCatalogStore() {
  // if (client) console.info('demo destroy')
  if (demo.optional) {
    demo.it.destroy()
    demo.close()
  } else {
    console.warn('no demo')
  }
}

export function createCatalogStore(data: DemoCatalogData, params: DemoCatalogParams) {
  // if (client) console.info('demo create')
  const store = new CatalogStore(data, params)
  demo.it = store
  if (client) void store.startup()
  return store
}
